import React, { useState, useEffect } from 'react';
import { Navbar } from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  TrendUp, 
  Tree, 
  Drop, 
  Fire, 
  Buildings, 
  Lightning, 
  Plant,
  GlobeHemisphereWest 
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const DashboardPage = () => {
  const { user, token } = useAuth();
  const [sdgMetrics, setSDGMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSDGMetrics();
  }, []);

  const fetchSDGMetrics = async () => {
    try {
      const response = await axios.get(`${API}/sdg/metrics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSDGMetrics(response.data);
    } catch (error) {
      console.error('Failed to fetch SDG metrics:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getRoleGreeting = () => {
    const greetings = {
      Government: 'Monitor national environmental and infrastructure goals',
      Enterprise: 'Track ESG metrics and supply chain sustainability',
      NGO: 'Measure impact and coordinate conservation efforts',
      Farmer: 'Optimize crop health and maximize yields',
      Developer: 'Build solutions with geospatial intelligence APIs'
    };
    return greetings[user?.role] || 'Intelligence for a Sustainable Planet';
  };

  const suiteIcons = {
    TerraVeritas: Tree,
    UrbIntellect: Buildings,
    AgriGrow: Plant,
    GeoCore: GlobeHemisphereWest
  };

  const sdgIcons = {
    2: Plant,
    6: Drop,
    7: Lightning,
    9: Buildings,
    11: Buildings,
    13: Fire,
    15: Tree
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-white/60 text-sm uppercase tracking-widest">Loading Dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="dashboard-page">
      <Navbar />
      
      <div className="max-w-[1920px] mx-auto p-6 space-y-6">
        {/* Welcome Section */}
        <div className="glass-panel rounded-sm p-8 border-l-4 border-emerald-500">
          <h1 className="text-3xl md:text-4xl font-black text-white mb-2 tracking-tight">
            Welcome back, {user?.name}
          </h1>
          <p className="text-gray-400 text-sm">{getRoleGreeting()}</p>
        </div>

        {/* SDG Impact Overview */}
        <div className="glass-panel rounded-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-white tracking-tight">SDG Impact Dashboard</h2>
              <p className="text-xs text-gray-400 uppercase tracking-wider mt-1">Real-time Global Metrics</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-400 uppercase tracking-wider">Total Impact Score</p>
              <p className="text-4xl font-black text-emerald-500">
                {sdgMetrics?.total_impact_score?.toFixed(1) || '0'}
              </p>
            </div>
          </div>

          {/* SDG Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-0 border-collapse">
            {sdgMetrics?.metrics.map((metric) => {
              const Icon = sdgIcons[metric.sdg_number] || TrendUp;
              return (
                <div 
                  key={metric.sdg_number} 
                  className="grid-border p-6 hover:bg-white/5 transition-colors"
                  data-testid={`sdg-metric-${metric.sdg_number}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="p-2 bg-emerald-500/10 rounded border border-emerald-500/20">
                      <Icon size={24} weight="duotone" className="text-emerald-500" />
                    </div>
                    <span className="text-xs font-bold text-emerald-500">SDG {metric.sdg_number}</span>
                  </div>
                  <h3 className="text-sm font-bold text-white mb-2">{metric.name}</h3>
                  <p className="text-2xl font-black text-white mb-1">
                    {metric.value.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </p>
                  <p className="text-xs text-gray-400">{metric.unit}</p>
                  <div className="mt-3 flex items-center gap-2">
                    <TrendUp size={14} className="text-emerald-500" />
                    <span className="text-xs text-emerald-500 uppercase tracking-wider">{metric.trend}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Quick Actions based on Role */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <a
            href="/map"
            className="glass-panel rounded-sm p-6 hover:bg-white/5 transition-all duration-200 border-l-2 border-emerald-500"
            data-testid="quick-action-map"
          >
            <GlobeHemisphereWest size={32} weight="duotone" className="text-emerald-500 mb-3" />
            <h3 className="text-lg font-bold text-white mb-1">Interactive Map</h3>
            <p className="text-xs text-gray-400">Explore geospatial intelligence</p>
          </a>

          <a
            href="/suites"
            className="glass-panel rounded-sm p-6 hover:bg-white/5 transition-all duration-200 border-l-2 border-blue-500"
            data-testid="quick-action-suites"
          >
            <Buildings size={32} weight="duotone" className="text-blue-500 mb-3" />
            <h3 className="text-lg font-bold text-white mb-1">Product Suites</h3>
            <p className="text-xs text-gray-400">Access specialized tools</p>
          </a>

          <a
            href="/sdg-tracker"
            className="glass-panel rounded-sm p-6 hover:bg-white/5 transition-all duration-200 border-l-2 border-amber-500"
            data-testid="quick-action-sdg"
          >
            <TrendUp size={32} weight="duotone" className="text-amber-500 mb-3" />
            <h3 className="text-lg font-bold text-white mb-1">SDG Tracker</h3>
            <p className="text-xs text-gray-400">Monitor sustainability goals</p>
          </a>

          <a
            href="/chat"
            className="glass-panel rounded-sm p-6 hover:bg-white/5 transition-all duration-200 border-l-2 border-purple-500"
            data-testid="quick-action-chat"
          >
            <GlobeHemisphereWest size={32} weight="duotone" className="text-purple-500 mb-3" />
            <h3 className="text-lg font-bold text-white mb-1">AI Assistant</h3>
            <p className="text-xs text-gray-400">Ask GeoAI anything</p>
          </a>
        </div>
      </div>
    </div>
  );
};
